import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Store } from '@ngxs/store';
import { map, Observable, Subject, Subscription } from 'rxjs';
import { CardStorageService } from 'src/app/common/services/storage/card-storage';
import { User } from 'src/app/common/state/user/user.model';
import { UserState } from 'src/app/common/state/user/user.state';
import { Animations } from 'src/app/common/utilities/animations';
import { v4 as uuidv4 } from 'uuid';
import { CompetencyDTO } from '../../../dtos/competencies.dto';
import { FileDTO } from '../../../dtos/file.dto';
import { FileType } from '../../../enums/file-type.enum';
import { InstantFeedbackService } from '../../../services/ai/instant-feedback.service';
import { UserPusherService } from '../../../services/pusher/user.pusher.service';
import {
  CompetencyFilterParams,
  TopicBuilderService,
} from '../../../services/topic-builder/topic-builder.service';
import { SmartFeedbackResponse } from '../../../types/responses/instant-feedback.responses';
import { FileListComponent } from '../../file-management/file-list/file-list.component';
import {
  FileUploadComponent,
  UploadStyles,
} from '../../file-management/file-upload/file-upload.component';

@Component({
  selector: 'app-card-smart-feedback',
  standalone: false,
  animations: [Animations.deelaayfadeInOut],
  templateUrl: './card-smart-feedback.component.html',
  styleUrl: './card-smart-feedback.component.scss',
})
export class CardSmartFeedbackComponent implements OnInit, OnDestroy {
  @ViewChild('uploadComponent') uploadComponent: FileUploadComponent;

  @ViewChild('fileListComponent') fileListComponent: FileListComponent;

  subs: Subscription[] = [];

  isCollapsed = this.cardStorageService.getCollapsedState('coacheelist');

  user: User;

  districtIds: number[] = [];

  // CUSTOM COMPETENCY
  competencyList: Observable<CompetencyDTO[]>;

  searchInput$ = new Subject<string>();

  selectedCompetency: CompetencyDTO | null = null;

  labelId = uuidv4();

  searchLoading = false;

  // ARTIFACT UPLOAD
  attachments: FileDTO[] = [];

  uploadStyle = UploadStyles.SIMPLE;

  allowedFileTypes = FileType.IMAGE;

  artifact: File | null;

  // NARRATIVE
  narrative: string | null;

  // SUBMIT RESPONSE
  readyToSubmit = false; // var needed to update template state

  submittingData = false;

  smartFeedbackId: number;

  smartFeedback?: SmartFeedbackResponse;

  errorMessage: string;

  loadingMessage: string;

  userFeedbackSubmitted = false;

  userFeedback: string | null;

  constructor(
    private cardStorageService: CardStorageService,
    private store: Store,
    private topicBuilderService: TopicBuilderService,
    private instantFeedbackService: InstantFeedbackService,
    private userPusherService: UserPusherService
  ) {
    this.user = this.store.selectSnapshot(UserState.getUser) as User;
  }

  ngOnInit(): void {
    if (this.user?.district) {
      this.districtIds.push(this.user.district.id);
    }

    this.searchInput$.subscribe((term) => {
      this.searchLoading = true;

      this.competencyList = this.loadCompetencyList(term);

      this.subs.push(
        this.competencyList.subscribe(() => {
          this.searchLoading = false;
        })
      );
    });

    this.searchInput$.next('');

    this.subs.push(
      this.userPusherService.feedbackUpdatedNotifier.subscribe((feedback) => {
        if (this.smartFeedbackId === feedback.feedbackId) {
          this.smartFeedback = feedback.smartFeedback;
          this.submittingData = false;
          this.loadingMessage = '';
        }
      })
    );
  }

  loadCompetencyList(searchTerm = ''): Observable<CompetencyDTO[]> {
    return this.topicBuilderService
      .getDistrictCompetencies(
        this.districtIds[0],
        { search: searchTerm } as CompetencyFilterParams,
        []
      )
      .pipe(
        map((response) =>
          [...response.items]
            .filter((comp) => comp.is_visible !== false)
            .sort((a, b) => {
              const rubricCompare = a.rubric_name.localeCompare(b.rubric_name);
              return rubricCompare !== 0
                ? rubricCompare
                : a.title.localeCompare(b.title);
            })
        )
      );
  }

  triggerCollapse() {
    this.isCollapsed = !this.isCollapsed;
    this.cardStorageService.storeCollapseState(
      'smartFeedback',
      this.isCollapsed
    );
  }

  updateAttachments(file: FileDTO) {
    this.attachments.push(file);
  }

  deleteAttachment(file: FileDTO) {
    this.attachments.forEach((attachment, index) => {
      if (attachment.id === file.id) {
        this.attachments.splice(index, 1);
      }
    });
    this.artifact = null;
    this.checkSubmissonStatus();
  }

  checkSubmissonStatus() {
    if ((this.artifact || this.narrative) && this.selectedCompetency) {
      this.readyToSubmit = true;
    } else {
      this.readyToSubmit = false;
    }
  }

  // Updates local property to be sent in API call on submit
  updateArtifact(file: File) {
    this.artifact = file;
    this.checkSubmissonStatus();
  }

  competencySelected() {
    this.checkSubmissonStatus();
  }

  getSmartFeedback() {
    if (
      this.readyToSubmit &&
      this.selectedCompetency &&
      (this.artifact || this.narrative) &&
      !this.submittingData
    ) {
      this.submittingData = true;

      this.instantFeedbackService
        .getCardFeedback(
          this.selectedCompetency.id,
          this.artifact,
          this.narrative
        )
        .subscribe({
          error: (error) => {
            this.errorMessage = error.error.message;
            this.submittingData = false;
          },
          next: (response) => {
            if ('error' in response) {
              this.errorMessage = response.error;
              this.submittingData = false;
            } else {
              this.beginLoadingMessages();
              this.smartFeedbackId = response.ai_instant_feedback_id;
            }
          },
        });
    }
  }

  beginLoadingMessages() {
    this.setLoadingMessage(
      `We're crafting feedback just for you ✨🛠️. Feel free to explore elsewhere in GroweLab 🧭, your feedback will be on the <a href="/dashboard/smart-feedback" target="_blank">Smart Feedback Page</a> when it's ready 📬`,
      5000
    );
    this.setLoadingMessage('', 19000);
    this.setLoadingMessage(
      `Hang tight—we're still whipping up your feedback 🧑‍🍳💬! Feel free to continue wandering around GroweLab or step away 🌀. It'll be ready for you soon on the <a href="/dashboard/smart-feedback" target="_blank">Smart Feedback Page</a> 🎁`,
      20000
    );
  }

  setLoadingMessage(message: string, timeout: number) {
    setTimeout(() => {
      if (this.submittingData) {
        this.loadingMessage = message;
      }
    }, timeout);
  }

  resetCard() {
    this.selectedCompetency = null;
    this.smartFeedback = undefined;
    this.artifact = null;
    this.attachments = [];
    this.narrative = null;
    this.loadingMessage = '';
    this.userFeedbackSubmitted = false;
    this.userFeedback = null;
    this.checkSubmissonStatus();
  }

  submitUserFeedback(direction: string) {
    this.instantFeedbackService
      .submitUserFeedback(this.smartFeedbackId, direction)
      .subscribe((resp) => {
        if (resp) {
          this.userFeedbackSubmitted = true;
          this.userFeedback = resp.user_feedback;
        }
      });
  }

  ngOnDestroy() {
    let sub = this.subs.pop();
    while (sub) {
      sub.unsubscribe();
      sub = this.subs.pop();
    }
  }
}
